import {
  useState, useEffect, useCallback, useMemo
} from 'react';

export default function useAddYoutubeScript(
  {
    videoId, autoplay = false, start = 0, playsinline = 1, iframeId,
  },
  onReadyCallback,
  onStateChangeCallback
) {
  const [player, setPlayer] = useState(null);

  // This effect will run once when the component mounts and will initialize the
  // youtube player. Changing the videoId is handled by the loadNewVideo
  // function exposed by the youtubeMethods object returned from this hook.
  useEffect(() => {
    function initializePlayer() {
      if (window.YT && window.YT.Player) {
        const newPlayer = new window.YT.Player(`youtube-video-${iframeId}`, {
          videoId,
          // Supported playerVars can be found here: https://developers.google.com/youtube/player_parameters#Parameters
          playerVars: {
            playsinline,
            autoplay: autoplay ? 1 : 0,
            start,
          },
          events: {
            onReady: onReadyCallback,
            onStateChange: onStateChangeCallback,
            onError: ({ data }) => {
              // eslint-disable-next-line no-console
              console.error(
                `Youtube player error: ${data}. See https://developers.google.com/youtube/iframe_api_reference#Events for more info.`
              );
            },
          },
        });
        setPlayer(newPlayer);
      }
    }

    if (
      !document.querySelector(
        'script[src="https://www.youtube.com/iframe_api"]'
      )
    ) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = initializePlayer;
    } else if (window.YT && window.YT.Player) {
      // Another YT video component has been loaded and the YT api is ready for use
      initializePlayer();
    } else if (window.onYouTubeIframeAPIReady) {
      // Another YT video component has been loaded but the player is not yet initialized.
      // Clone the old callback function from our other component and then add in this one so both videos get initialized
      const oldCallback = window.onYouTubeIframeAPIReady.bind({});
      window.onYouTubeIframeAPIReady = () => {
        oldCallback();
        initializePlayer();
      };
    } else {
      // eslint-disable-next-line no-console
      console.error(`LDS Error - useAddYoutubeScript. Error loading youtube video ${videoId}`);
    }
  }, [autoplay, iframeId, onReadyCallback, onStateChangeCallback, playsinline, start, videoId]);

  // We need useCallback and use memo below to prevent functions and
  // youtubeMethods object from being recreated on every render causing an infinite
  // loop

  // See https://developers.google.com/youtube/iframe_api_reference#Playback_controls
  // for more info on methods available on the player object

  const seekToPosition = useCallback(
    (seconds, allowSeekAhead = true) => {
      if (player && typeof player.seekTo === 'function') {
        player.seekTo(seconds, allowSeekAhead);
      }
    },
    [player]
  );

  const loadNewVideo = useCallback(
    (newVideoId) => {
      if (player && typeof player.loadVideoById === 'function') {
        player.loadVideoById(newVideoId);
      }
    },
    [player]
  );
  // combine functions into an object to return and memoize it
  const youtubeMethods = useMemo(
    () => ({
      seekToPosition,
      loadNewVideo,
    }),
    [seekToPosition, loadNewVideo]
  );

  return { youtubeMethods };
}
